import { useState, useEffect } from 'react';
import { useSite } from '../contexts/SiteContext';
import { useQuery } from '@tanstack/react-query';
import { pages, domains } from '../lib/api';
import { ChecklistItem } from '../components/OnboardingChecklist';

export function useOnboardingProgress() {
  const { selectedSite, sites } = useSite();
  const [dismissed, setDismissed] = useState(
    localStorage.getItem('onboarding_checklist_dismissed') === 'true'
  );

  const { data: pagesData } = useQuery({
    queryKey: ['pages', selectedSite?.id],
    queryFn: () => pages.list(selectedSite?.id),
    enabled: !!selectedSite?.id,
  });

  const { data: domainsData } = useQuery({
    queryKey: ['domains', selectedSite?.id],
    queryFn: () => domains.list(selectedSite?.id),
    enabled: !!selectedSite?.id,
  });

  const hasSite = sites && sites.length > 0;
  const hasPages = pagesData?.pages && pagesData.pages.length > 0;
  const hasPublishedPages = pagesData?.pages?.some((p: any) => p.published === 1) || false;
  const hasCustomDomain = domainsData?.domains && domainsData.domains.length > 0;
  const hasCustomizedDesign = localStorage.getItem('design_customized') === 'true';

  const items: ChecklistItem[] = [
    {
      id: 'create_site',
      label: 'Create your first site',
      done: hasSite,
      link: hasSite ? undefined : '/wizard?new=true',
    },
    {
      id: 'add_pages',
      label: 'Add pages to your site',
      done: hasPages,
      link: hasPages || !hasSite ? undefined : '/pages',
    },
    {
      id: 'customize_design',
      label: 'Customize your design',
      done: hasCustomizedDesign,
      link: hasCustomizedDesign || !hasSite ? undefined : '/design',
    },
    {
      id: 'publish_pages',
      label: 'Publish your pages',
      done: hasPublishedPages,
      link: hasPublishedPages || !hasPages ? undefined : '/pages',
    },
    {
      id: 'add_domain',
      label: 'Connect a custom domain',
      done: hasCustomDomain || false,
      link: hasCustomDomain || !hasSite ? undefined : '/domains',
    },
  ];

  const completedCount = items.filter((item) => item.done).length;
  const isComplete = completedCount === items.length;

  const handleDismiss = () => {
    localStorage.setItem('onboarding_checklist_dismissed', 'true');
    setDismissed(true);
  };

  const showChecklist = !dismissed && !isComplete && hasSite;

  return {
    items,
    completedCount,
    isComplete,
    showChecklist,
    handleDismiss,
  };
}
